/**
 * Settings (Graphic/Settings.png) and its sub-pages:
 *   #/settings                 hub
 *   #/settings/personal        name, email, sign-in method, delete account
 *   #/settings/notifications   what to be told about (stored on this device)
 *   #/settings/language        English (more later)
 *   #/settings/quality         default video quality
 *   #/settings/help            FAQ + contact
 *
 * Left out on purpose: Change Password (sign-in is by email link, there is no
 * password) and Appearance (the app is dark-only for now).
 */
import type { QualityTier } from '@loomia/shared';
import { api } from '../api/client.js';
import { getUser, hasAccount, signOut } from '../state/auth.js';
import { clearBalance } from '../state/credits.js';
import { getPrefs, setPrefs } from '../state/prefs.js';
import { navigate } from '../router.js';
import { toast, toastApiError } from '../ui/toast.js';
import { openSignInSheet } from '../ui/signinSheet.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { SITE_URL, SUPPORT_EMAIL } from '../config.js';
import { displayName } from './profile.js';

const APP_VERSION = '0.1.0';
const TIERS: { id: QualityTier; hint: string }[] = [
  { id: '360p', hint: 'Cheapest' },
  { id: '540p', hint: 'Balanced' },
  { id: '720p', hint: 'HD' },
  { id: '1080p', hint: 'Full HD' },
];

const FAQ: { q: string; a: string }[] = [
  { q: 'What are credits (✦)?', a: 'Credits pay for each creation. The cost is shown on the Generate button before you start. If a run fails, the credits are refunded automatically.' },
  { q: 'How do I get free credits?', a: 'Open Daily Reward from the gift button or your Profile and check in every day. The longer your streak, the bigger the reward.' },
  { q: 'Where are my creations?', a: 'In the Collection tab. You can search, filter by images or videos, and heart your favourites.' },
  { q: 'How long does a video take?', a: 'Usually a few minutes. You can leave the screen; the result waits in Collection.' },
  { q: 'Why was my photo rejected?', a: 'Use a clear, well-lit photo with the subject visible. Very small or very large images are not accepted.' },
  { q: 'How do I delete my account?', a: 'Settings → Personal Information → Delete account. This removes your creations and credits permanently.' },
];

function row(ic: string, title: string, sub: string, href: string, value?: string, attrs = ''): string {
  return `
    <a class="st-row" href="${href}" ${attrs}>
      <span class="st-ic">${icon(ic)}</span>
      <span class="st-tx"><strong>${title}</strong><small>${sub}</small></span>
      ${value ? `<span class="st-val">${escapeHtml(value)}</span>` : ''}
      ${icon('chevron_right', { cls: 'st-go' })}
    </a>`;
}

function header(title: string, sub: string, back: string): string {
  return `
    <header class="st-head">
      <a class="st-back" href="${back}" aria-label="Back">${icon('arrow_back')}</a>
      <div><h1 class="st-title">${title}</h1><p class="st-sub">${sub}</p></div>
    </header>`;
}

function toggleHtml(key: string, title: string, sub: string, on: boolean): string {
  return `
    <label class="st-row st-toggle">
      <span class="st-tx"><strong>${title}</strong><small>${sub}</small></span>
      <input type="checkbox" data-pref="${key}" ${on ? 'checked' : ''} />
      <span class="st-switch" aria-hidden="true"></span>
    </label>`;
}

export function renderSettings(root: HTMLElement, sub: string): void {
  if (!hasAccount()) {
    root.innerHTML = `
      <div class="settings">
        ${header('Settings', 'Customize your experience', '#/home')}
        <div class="empty">
          <p class="muted" style="max-width: 280px;">Sign in to manage your account and preferences.</p>
          <button class="btn btn-primary" id="st-signin" type="button">Sign in</button>
        </div>
      </div>`;
    root.querySelector('#st-signin')?.addEventListener('click', () => openSignInSheet('Sign in to open settings.'));
    return;
  }
  switch (sub) {
    case 'personal':
      return renderPersonal(root);
    case 'notifications':
      return renderNotifications(root);
    case 'language':
      return renderLanguage(root);
    case 'quality':
      return renderQuality(root);
    case 'help':
      return renderHelp(root);
    default:
      return renderHub(root);
  }
}

function renderHub(root: HTMLElement): void {
  const email = getUser()?.email ?? '';
  const prefs = getPrefs();
  root.innerHTML = `
    <div class="settings">
      ${header('Settings', 'Customize your experience', '#/profile')}
      <a class="st-me" href="#/settings/personal">
        <span class="st-avatar" aria-hidden="true">${escapeHtml(displayName(email).charAt(0))}</span>
        <span class="st-tx"><strong>${escapeHtml(displayName(email))}</strong><small>${escapeHtml(email)}</small></span>
        ${icon('chevron_right', { cls: 'st-go' })}
      </a>

      <h2 class="st-group">Account</h2>
      <div class="st-card">
        ${row('person', 'Personal Information', 'Name, email, delete account', '#/settings/personal')}
        ${row('workspace_premium', 'Credits & Plans', 'Buy credits and manage your plan', '#/paywall')}
      </div>

      <h2 class="st-group">Preferences</h2>
      <div class="st-card">
        ${row('notifications', 'Notifications', 'Manage your notifications', '#/settings/notifications')}
        ${row('language', 'Language', 'Select your preferred language', '#/settings/language', 'English')}
        ${row('imagesmode', 'Output Quality', 'Set default video quality', '#/settings/quality', prefs.quality)}
      </div>

      <h2 class="st-group">Support</h2>
      <div class="st-card">
        ${row('help', 'Help & Support', 'FAQs, contact us', '#/settings/help')}
        ${row('description', 'Terms of Service', 'Learn about our terms', `${SITE_URL}/legal/terms.html`, undefined, 'target="_blank" rel="noopener"')}
        ${row('verified_user', 'Privacy Policy', 'How we protect your data', `${SITE_URL}/legal/privacy.html`, undefined, 'target="_blank" rel="noopener"')}
        ${row('logout', 'Log Out', 'Sign out from your account', '#/settings', undefined, 'data-act="logout"')}
      </div>
      <p class="muted st-ver">Loomia ${APP_VERSION}</p>
    </div>`;

  root.querySelector('[data-act="logout"]')?.addEventListener('click', (e) => {
    e.preventDefault();
    void signOut().then(() => {
      clearBalance();
      toast('Signed out.');
      navigate('/home');
    });
  });
}

function renderPersonal(root: HTMLElement): void {
  const email = getUser()?.email ?? '';
  root.innerHTML = `
    <div class="settings">
      ${header('Personal Information', 'Your account details', '#/settings')}
      <div class="st-card st-pad">
        <label class="st-field">Name
          <input id="st-name" type="text" maxlength="40" value="${escapeHtml(getPrefs().displayName)}" placeholder="${escapeHtml(displayName(email))}" />
          <small>Shown on your profile. Saved on this device.</small>
        </label>
        <div class="st-field">Email<div class="st-static">${escapeHtml(email)}</div></div>
        <div class="st-field">Sign-in<div class="st-static">Email link — no password to remember</div></div>
      </div>
      <button class="btn btn-primary btn-block" id="st-save" type="button">Save</button>
      <h2 class="st-group">Danger zone</h2>
      <div class="st-card st-pad">
        <p class="muted">Deleting your account permanently removes your creations, credits and daily rewards.</p>
        <button class="btn btn-secondary btn-block st-danger" id="st-delete" type="button">Delete account</button>
      </div>
    </div>`;
  root.querySelector('#st-save')?.addEventListener('click', () => {
    setPrefs({ displayName: (root.querySelector<HTMLInputElement>('#st-name')?.value ?? '').trim() });
    toast('Saved.');
    navigate('/settings');
  });
  root.querySelector('#st-delete')?.addEventListener('click', () => {
    const ok = window.confirm('Delete your account? Your creations, credits and daily rewards will be permanently removed. This cannot be undone.');
    if (!ok) return;
    void api
      .deleteAccount()
      .then(() => signOut())
      .then(() => {
        clearBalance();
        toast('Your account was deleted.');
        navigate('/home');
      })
      .catch((err) => toastApiError(err));
  });
}

function renderNotifications(root: HTMLElement): void {
  const p = getPrefs();
  root.innerHTML = `
    <div class="settings">
      ${header('Notifications', 'Choose what you hear about', '#/settings')}
      <div class="st-card">
        ${toggleHtml('notifyJobDone', 'Creation ready', 'When a video or photo finishes', p.notifyJobDone)}
        ${toggleHtml('notifyDaily', 'Daily reward reminder', 'Don’t lose your streak', p.notifyDaily)}
        ${toggleHtml('notifyNews', 'News & offers', 'New effects and promotions', p.notifyNews)}
      </div>
      <p class="muted st-note">Your choices are saved on this device. Push alerts start working with the mobile app.</p>
    </div>`;
  root.querySelectorAll<HTMLInputElement>('input[data-pref]').forEach((el) =>
    el.addEventListener('change', () => setPrefs({ [el.dataset['pref']!]: el.checked })),
  );
}

function renderLanguage(root: HTMLElement): void {
  root.innerHTML = `
    <div class="settings">
      ${header('Language', 'Select your preferred language', '#/settings')}
      <div class="st-card">
        <div class="st-row"><span class="st-tx"><strong>English</strong></span>${icon('check', { cls: 'st-ok' })}</div>
        <div class="st-row st-off"><span class="st-tx"><strong>Tiếng Việt</strong><small>Coming soon</small></span></div>
      </div>
    </div>`;
}

function renderQuality(root: HTMLElement): void {
  const draw = (): void => {
    const cur = getPrefs().quality;
    root.innerHTML = `
      <div class="settings">
        ${header('Output Quality', 'Default video quality', '#/settings')}
        <div class="st-card">
          ${TIERS.map(
            (t) => `
            <button class="st-row st-pick" type="button" data-q="${t.id}">
              <span class="st-tx"><strong>${t.id}</strong><small>${t.hint}</small></span>
              ${t.id === cur ? icon('check', { cls: 'st-ok' }) : ''}
            </button>`,
          ).join('')}
        </div>
        <p class="muted st-note">Used as the starting choice in Image To Video. Higher quality costs more credits, and you can change it for each creation.</p>
      </div>`;
    root.querySelectorAll<HTMLElement>('[data-q]').forEach((b) =>
      b.addEventListener('click', () => {
        setPrefs({ quality: b.dataset['q'] as QualityTier });
        draw();
      }),
    );
  };
  draw();
}

function renderHelp(root: HTMLElement): void {
  root.innerHTML = `
    <div class="settings">
      ${header('Help & Support', 'FAQs, contact us', '#/settings')}
      <div class="st-card">
        ${FAQ.map((f) => `<details class="st-faq"><summary>${escapeHtml(f.q)}</summary><p>${escapeHtml(f.a)}</p></details>`).join('')}
      </div>
      <h2 class="st-group">Contact us</h2>
      <div class="st-card st-pad">
        ${
          SUPPORT_EMAIL
            ? `<a class="btn btn-primary btn-block" href="mailto:${escapeHtml(SUPPORT_EMAIL)}?subject=Loomia%20support">Email ${escapeHtml(SUPPORT_EMAIL)}</a>`
            : '<p class="muted">Support email is coming soon.</p>'
        }
      </div>
    </div>`;
}
